import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, CheckCircle2, Clock, AlertTriangle, PauseCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtErr = (d) => (typeof d === "string" ? d : d?.msg || "Something went wrong.");
const fmt = (d) => new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });

const UploadRow = ({ token, item, onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/candidate-review/hold/${token}/upload/${item.id}`, fd);
      toast.success(`${item.label} uploaded`);
      onDone();
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className={`flex items-center justify-between gap-3 border px-4 py-3 ${item.flagged && !item.uploaded_now ? "border-amber-500/50 bg-amber-500/5" : "border-white/10"}`} data-testid={`hold-item-${item.id}`}>
      <div className="min-w-0">
        <p className="text-sm text-white">{item.label}</p>
        <p className="text-[11px] text-slate-400 mt-0.5">
          {item.uploaded_now ? <span className="text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Uploaded just now</span>
            : item.flagged ? <span className="text-amber-300">Needs to be re-uploaded{item.reason ? ` — ${item.reason}` : ""}</span>
            : item.uploaded_at ? `Previously uploaded ${fmt(item.uploaded_at)}` : "Not uploaded yet"}
        </p>
      </div>
      <label className={`shrink-0 inline-flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-3 py-2 border cursor-pointer ${busy ? "opacity-60" : ""} border-nx-gold/60 text-nx-goldlight hover:bg-nx-gold/10`} data-testid={`hold-upload-${item.id}`}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} {item.uploaded_now || item.uploaded_at ? "Replace" : "Upload"}
        <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={pick} disabled={busy} />
      </label>
    </div>
  );
};

export default function HoldUpload() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/candidate-review/hold/${token}`);
      setData(data);
      setError("");
    } catch (err) {
      setError(fmtErr(err?.response?.data?.detail) || "This link is not valid.");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (!token) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-nx-navy flex items-center justify-center px-4 py-12" data-testid="hold-upload-page">
      <div className="w-full max-w-xl">
        <img src="/assets/logo-signature-dark.png" alt="NexSecure Technologies" className="h-24 mx-auto mb-6" />
        <div className="border border-white/10 bg-white/[0.03] p-5 sm:p-8">
          {error ? (
            <div className="text-center py-6" data-testid="hold-expired">
              <AlertTriangle size={30} className="mx-auto text-red-400" />
              <h1 className="mt-4 text-white text-lg">Link not available</h1>
              <p className="mt-2 text-sm text-slate-400">{error}</p>
              <p className="mt-4 text-xs text-slate-500">Questions? Write to hr@nexsecure.in</p>
            </div>
          ) : !data ? (
            <p className="text-slate-400 text-sm text-center py-10">Loading…</p>
          ) : (
            <>
              <p className="text-[10px] tracking-[0.28em] uppercase text-nx-goldlight flex items-center gap-2"><PauseCircle size={13} /> Appointment on hold</p>
              <h1 className="mt-3 text-white text-xl font-medium" data-testid="hold-candidate-name">Hello {data.employee_name}</h1>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">Your appointment for <b>{data.designation}</b> has been placed on hold. Please upload the required document(s) below.</p>
              <blockquote className="mt-4 border-l-2 border-nx-gold pl-4 text-sm text-slate-200 leading-relaxed" data-testid="hold-reason">{data.reason}</blockquote>
              <p className={`mt-4 text-xs flex items-center gap-2 ${data.status === "pending_review" ? "text-emerald-300" : "text-amber-300"}`} data-testid="hold-expiry">
                <Clock size={13} /> {data.status === "pending_review" ? "Documents received — under review. " : ""}This link is valid for {data.window} — until {fmt(data.expires_at)}.
              </p>
              <div className="mt-6 flex flex-col gap-2" data-testid="hold-items">
                {data.items.map((it) => <UploadRow key={it.id} token={token} item={it} onDone={load} />)}
              </div>
              <p className="mt-5 text-[11px] text-slate-500 leading-relaxed">Accepted: PDF, JPG, PNG or WEBP up to 10MB. Each upload is checked automatically to confirm it is the requested document. {data.auto_cancel ? "If nothing is uploaded before the link expires, the appointment will be cancelled automatically." : "If nothing is uploaded before the link expires, the matter is escalated to the CEO."}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
